import React, { useEffect, useRef, useState } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { FallbackMap } from './fallback-map';

interface ThreatLocation {
  lat: number;
  lng: number;
  region: string;
  threats: number;
  severity: 'low' | 'medium' | 'high';
  details: string[];
}

interface GoogleMapsProps {
  threatData: ThreatLocation[];
  height?: string;
  className?: string;
}

// Google Maps API key - Load from environment variable
const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || 'AIzaSyC0X60eWfZc0tJyi9Zos5ePJj7B5Hjgv0Y';

const render = (status: Status) => {
  console.log('Google Maps Status:', status);
  switch (status) {
    case Status.LOADING:
      return (
        <div className="flex items-center justify-center h-full bg-slate-100 dark:bg-slate-800 rounded-lg">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto mb-4"></div>
            <p className="text-slate-600 dark:text-slate-400">Loading Google Maps...</p>
            <p className="text-xs text-slate-500 mt-2">API Key: {GOOGLE_MAPS_API_KEY.substring(0, 10)}...</p>
          </div>
        </div>
      );
    case Status.FAILURE:
      return (
        <div className="flex items-center justify-center h-full bg-red-50 dark:bg-red-900/20 rounded-lg border-2 border-red-200">
          <div className="text-center p-4">
            <div className="text-red-500 mb-2 text-4xl">⚠️</div>
            <p className="text-red-600 dark:text-red-400 font-semibold">Google Maps Failed to Load</p>
            <p className="text-sm text-red-500 dark:text-red-400 mt-1">
              Check API key or network connection
            </p>
            <p className="text-xs text-red-400 mt-2">
              API Key: {GOOGLE_MAPS_API_KEY.substring(0, 10)}...
            </p>
          </div>
        </div>
      );
    default:
      return null;
  }
};

const MapComponent: React.FC<{ threatData: ThreatLocation[] }> = ({ threatData }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  console.log('MapComponent threatData:', threatData);
  console.log('Google Maps available:', !!window.google);

  // Timeout to show fallback if map doesn't load
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!mapLoaded) {
        console.log('Google Maps timeout - showing fallback');
        const fallback = document.getElementById('fallback-map');
        if (fallback) {
          fallback.style.display = 'flex';
        }
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timer);
  }, [mapLoaded]);

  useEffect(() => {
    if (!mapRef.current || !window.google) {
      console.log('Google Maps not ready:', { 
        mapRef: !!mapRef.current, 
        google: !!window.google 
      });
      return;
    }

    console.log('Initializing Google Maps...');

    // Initialize map
    const map = new google.maps.Map(mapRef.current, {
      center: { lat: 20, lng: 0 },
      zoom: 2,
      styles: [
        {
          featureType: 'water',
          elementType: 'geometry',
          stylers: [{ color: '#dbeafe' }]
        },
        {
          featureType: 'landscape',
          elementType: 'geometry',
          stylers: [{ color: '#f8fafc' }]
        },
        {
          featureType: 'road',
          elementType: 'geometry',
          stylers: [{ color: '#e2e8f0' }]
        }
      ],
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
      gestureHandling: 'greedy',
      clickableIcons: false,
    });

    mapInstanceRef.current = map;
    setMapLoaded(true);

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Add threat markers with enhanced animations and visual effects
    threatData.forEach((threat, index) => {
      const severityColors = {
        low: '#10b981',    // green
        medium: '#f59e0b',  // amber
        high: '#ef4444'     // red
      };

      // Create pulsing animation effect
      const pulseAnimation = () => {
        const markerElement = document.querySelector(`[data-marker-id="${threat.region}"]`);
        if (markerElement) {
          markerElement.style.animation = 'pulse 2s infinite';
        }
      };

      const marker = new google.maps.Marker({
        position: { lat: threat.lat, lng: threat.lng },
        map: map,
        title: `${threat.region}: ${threat.threats} threats`,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: Math.max(12, Math.min(25, threat.threats * 3)),
          fillColor: severityColors[threat.severity],
          fillOpacity: 0.9,
          strokeColor: '#ffffff',
          strokeWeight: 4,
        },
        animation: google.maps.Animation.DROP,
        label: {
          text: threat.threats.toString(),
          color: 'white',
          fontWeight: 'bold',
          fontSize: '14px'
        }
      });

      // Add pulsing animation after marker is created
      setTimeout(() => {
        if (threat.severity === 'high' || threat.severity === 'critical') {
          marker.setAnimation(google.maps.Animation.BOUNCE);
          setTimeout(() => marker.setAnimation(null), 2000);
        }
      }, index * 200);

      // Add info window
      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div class="p-3 min-w-[200px]">
            <h3 class="font-semibold text-slate-900 mb-2">${threat.region}</h3>
            <div class="space-y-1">
              <div class="flex items-center gap-2">
                <div class="w-3 h-3 rounded-full" style="background-color: ${severityColors[threat.severity]}"></div>
                <span class="text-sm font-medium">${threat.threats} Security Threats</span>
              </div>
              <div class="text-xs text-slate-600 mt-2">
                <div>Severity: <span class="font-medium capitalize">${threat.severity}</span></div>
                <div>Region: <span class="font-medium">${threat.region}</span></div>
              </div>
              ${threat.details.length > 0 ? `
                <div class="mt-2 text-xs text-slate-500">
                  ${threat.details.map(detail => `<div>• ${detail}</div>`).join('')}
                </div>
              ` : ''}
            </div>
          </div>
        `,
      });

      // Add click listener
      marker.addListener('click', () => {
        infoWindow.open(map, marker);
      });

      // Add hover effects
      marker.addListener('mouseover', () => {
        setHoveredRegion(threat.region);
        marker.setAnimation(google.maps.Animation.BOUNCE);
      });

      marker.addListener('mouseout', () => {
        setHoveredRegion(null);
        marker.setAnimation(null);
      });

      markersRef.current.push(marker);
    });

    // Fit map to show all markers
    if (threatData.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      threatData.forEach(threat => {
        bounds.extend({ lat: threat.lat, lng: threat.lng });
      });
      map.fitBounds(bounds);
    }

    console.log('Google Maps initialized successfully with', threatData.length, 'threats');

  }, [threatData]);

  // Retry mechanism if Google Maps fails to load
  useEffect(() => {
    const checkGoogleMaps = () => {
      if (!window.google && !mapLoaded) {
        console.log('Google Maps not loaded, retrying...');
        setTimeout(checkGoogleMaps, 1000);
      }
    };
    
    checkGoogleMaps();
  }, [mapLoaded]);

  return (
    <div className="relative w-full h-full">
      <div ref={mapRef} className="w-full h-full rounded-lg" />
      
      {/* Hovered region info */}
      {hoveredRegion && (
        <div className="absolute top-4 left-4 bg-white dark:bg-slate-800 rounded-lg shadow-lg p-3 z-10 border border-slate-200 dark:border-slate-700 animate-fadeIn">
          <div className="text-sm font-medium text-slate-900 dark:text-white">
            {hoveredRegion}
          </div>
          <div className="text-xs text-slate-600 dark:text-slate-400">
            Hovering over threat region
          </div>
        </div>
      )}

      {/* Live data indicator with enhanced animation */}
      <div className="absolute top-2 right-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg z-10 animate-pulse">
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 bg-white rounded-full animate-ping"></div>
          <span className="animate-pulse">Live Threat Data</span>
        </div>
      </div>

      {/* Enhanced Legend with animations */}
      <div className="absolute bottom-2 right-2 bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm rounded-lg shadow-lg p-3 z-10 border border-slate-200 dark:border-slate-700">
        <div className="text-xs font-medium text-slate-900 dark:text-white mb-2 flex items-center gap-1">
          <div className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-ping"></div>
          Global Threats
        </div>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span className="text-xs text-slate-600 dark:text-slate-400">Low Risk</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-amber-500"></div>
            <span className="text-xs text-slate-600 dark:text-slate-400">Medium Risk</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span className="text-xs text-slate-600 dark:text-slate-400">High Risk</span>
          </div>
        </div>
      </div>

      {/* Threat counter without animation */}
      <div className="absolute top-2 left-2 bg-red-500/90 text-white px-3 py-1.5 rounded-lg text-sm font-bold shadow-lg z-10">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-white rounded-full"></div>
          {threatData.length} Active Threats
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export const GoogleMaps: React.FC<GoogleMapsProps> = ({ 
  threatData, 
  height = "400px", 
  className = "" 
}) => {
  console.log('GoogleMaps component rendering with threatData:', threatData);
  
  return (
    <div className={`relative ${className}`} style={{ height, width: '100%' }}>
      <div className="relative w-full h-full rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 shadow-lg bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-slate-800 dark:to-slate-900">
        <Wrapper 
          apiKey={GOOGLE_MAPS_API_KEY} 
          render={render}
          libraries={['maps']}
        >
          <MapComponent threatData={threatData} />
        </Wrapper>
      </div>
    </div>
  );
};
